import { useState } from 'react'
import clsx from 'clsx'
import { useMediaQuery } from '@hotmart/space-lib/dist/hooks'
import {
  BlockedCommentsTag,
  PostCommentInput,
  PostInteractionCounters,
  PostReactions
} from 'src/components'
import { queryClient } from 'src/config/request'
import { CommentType } from 'src/components/postComments'
import { useUser } from 'src/hook/useUser'
import i18n from 'src/i18n'
import { convertNumberToCompactFormat } from 'src/utils/convertNumberToCompactFormat'
import { ScreenSizes } from 'src/styles'
import { FeedCardFooterProps } from './feedCardFooter.types'
import { ViewComments } from './feedCardFooter.styles'

const FeedCardFooter = ({
  post,
  enableMentions,
  enableCommentSuggestion,
  enableCommentInput,
  onClickCommentCounter
}: FeedCardFooterProps) => {
  const [commentsCount, setCommentsCount] = useState(post.commentsCount ?? 0)
  const { isOwnerOrAdminOrModerator } = useUser()
  const isMobile = useMediaQuery(`(max-width: ${ScreenSizes.md})`)

  const allowMedias = [CommentType.IMAGE, CommentType.DOCUMENT, CommentType.AUDIO]
  const isCommentsEnabled = Boolean(post?.isCommentsEnabled)
  const showCommentInput = enableCommentInput && (isCommentsEnabled || isOwnerOrAdminOrModerator)
  const showCommentCounter = Boolean(isCommentsEnabled || isOwnerOrAdminOrModerator)
  const showViewCommentsCounter = Boolean(
    (isCommentsEnabled || isOwnerOrAdminOrModerator) && commentsCount > 0
  )
  const showCommentsStatus = showViewCommentsCounter || !isCommentsEnabled

  const handleCommentSuccess = () => {
    setCommentsCount((prevState) => prevState + 1)
    queryClient.invalidateQueries({
      queryKey: ['getPosts']
    })
  }

  return (
    <footer className="_border-top _border-gray-200">
      <div
        className={clsx(
          '_d-flex _align-items-start _align-items-md-center _justify-content-between _flex-column-reverse _flex-md-row',
          {
            '_p-4': !isMobile
          }
        )}
      >
        <PostInteractionCounters
          className={clsx({ '_p-4': isMobile })}
          comments={commentsCount}
          views={post?.viewsCount}
          enabledComments={showCommentCounter}
          onClickCommentCounter={onClickCommentCounter}
        />

        <PostReactions
          className={clsx({ '_w-full _p-4 _border-bottom _border-gray-200': isMobile })}
          postId={post.id}
          reactions={post?.reactions}
        />
      </div>

      {showCommentInput && (
        <div className="_border-top _border-gray-200 _p-4">
          <PostCommentInput
            idPost={post.id}
            allowMedias={allowMedias}
            authorRole={post.authorMemberType}
            authorName={post?.authorName}
            enableMentions={enableMentions}
            commentSuggestion={enableCommentSuggestion}
            onSuccess={handleCommentSuccess}
          />
        </div>
      )}

      {showCommentsStatus && (
        <div
          className={clsx(
            '_d-flex _align-items-start _align-items-md-center _border-top _border-gray-200 _flex-column-reverse _flex-md-row',
            {
              '_p-4': !isMobile
            }
          )}
        >
          {showViewCommentsCounter && (
            <div
              className={clsx({
                '_p-4': isMobile
              })}
            >
              <ViewComments
                className="_rounded _cursor-pointer _px-4 _py-2"
                onClick={onClickCommentCounter}
              >
                {commentsCount === 1
                  ? i18n.t('feed.card.comments.show_single_comment')
                  : i18n.t('feed.card.comments.show_multiple_comments', {
                      comments_count: convertNumberToCompactFormat(commentsCount)
                    })}
              </ViewComments>
            </div>
          )}

          {!isCommentsEnabled && (
            <div
              className={clsx('_ml-md-auto', {
                '_w-full _p-4': isMobile,
                '_border-bottom _border-gray-200': isMobile && showViewCommentsCounter
              })}
            >
              <BlockedCommentsTag />
            </div>
          )}
        </div>
      )}
    </footer>
  )
}

export default FeedCardFooter
